import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { FocusTrap } from 'focus-trap-react';

interface CreditControllingPoliciesStepProps {
  onboardingStatus: any;
  onNextStep?: () => void;
  publicMode?: boolean;
}

const CreditControllingPoliciesStep: React.FC<CreditControllingPoliciesStepProps> = ({
  onboardingStatus,
  onNextStep,
  publicMode = false
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [markdownContent, setMarkdownContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [comments, setComments] = useState<{ [sectionId: string]: string }>({});
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [currentSectionId, setCurrentSectionId] = useState<string>('');
  const [currentComment, setCurrentComment] = useState<string>('');
  const [currentSectionTitle, setCurrentSectionTitle] = useState<string>('');
  const markdownRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const getStepStatus = () => {
    if (!onboardingStatus?.xero_authorization?.is_authorized) {
      return 'waiting';
    }
    
    return 'active';
  };

  const status = getStepStatus();

  // Comment handling functions
  const handleCommentClick = (sectionId: string, sectionTitle: string) => {
    setCurrentSectionId(sectionId);
    setCurrentSectionTitle(sectionTitle);
    setCurrentComment(comments[sectionId] || '');
    setShowCommentModal(true);
  };

  const handleSaveComment = () => {
    if (currentComment.trim()) {
      setComments(prev => ({
        ...prev,
        [currentSectionId]: currentComment.trim()
      }));
    } else {
      // Remove comment if empty
      setComments(prev => {
        const newComments = { ...prev };
        delete newComments[currentSectionId];
        return newComments;
      });
    }
    setShowCommentModal(false);
    setCurrentComment('');
    setCurrentSectionId('');
  };

  const handleCancelComment = () => {
    setShowCommentModal(false);
    setCurrentComment('');
    setCurrentSectionId('');
    setCurrentSectionTitle('');
  };

  // Focus management and escape key handling for comment modal
  useEffect(() => {
    if (showCommentModal) {
      // Set focus to textarea when modal opens
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
      
      // Prevent background scrolling
      document.body.style.overflow = 'hidden';
      
      // Handle escape key
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          handleCancelComment();
        }
      };
      
      document.addEventListener('keydown', handleEscape);
      
      // Cleanup
      return () => {
        document.body.style.overflow = 'unset';
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [showCommentModal]);

  // Utility to extract plain text from ReactMarkdown h1 children
  const extractText = (children: any): string => {
    if (typeof children === 'string') return children;
    if (Array.isArray(children)) return children.map(extractText).join('');
    if (children && typeof children === 'object' && 'props' in children) {
      // @ts-ignore
      return extractText(children.props?.children);
    }
    return '';
  };

  // Custom H2 rendered by ReactMarkdown (pure React, no DOM mutation)
  const H2WithComment = (props: any) => {
    const textContent = extractText(props.children);
    const sectionId = `section-${textContent
      .replace(/[^a-zA-Z0-9\s]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .toLowerCase() || 'untitled'}`;
    const hasComment = !!comments[sectionId];

    return (
      <div className="flex items-center justify-between group" style={{ marginTop: '0.5rem', marginBottom: '0.25rem' }}>
        <h2
          {...props}
          style={{
            fontSize: '1.25rem',
            fontWeight: 'bold',
            marginTop: 0,
            marginBottom: 0,
            color: '#1f2937',
            flex: 1
          }}
        />
        <button
          onClick={() => handleCommentClick(sectionId, textContent)}
          className={`ml-2 sm:ml-4 p-1.5 sm:p-2 rounded-full transition-colors ${
            hasComment ? 'bg-blue-100 text-blue-600 hover:bg-blue-200' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
          }`}
          title={hasComment ? 'Edit comment' : 'Add comment'}
          type="button"
        >
          {hasComment ? (
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          )}
        </button>
      </div>
    );
  };

  // Load markdown content
  useEffect(() => {
    const loadMarkdownContent = async () => {
      try {
        const response = await fetch('/docs/credit_controling_policies_v1.md');
        if (response.ok) {
          const content = await response.text();
          setMarkdownContent(content);
        } else {
          console.error('Failed to load markdown file, status:', response.status);
          setMarkdownContent('# Error\n\nFailed to load the credit controlling policies document.');
        }
      } catch (error) {
        console.error('Error loading markdown file:', error);
        setMarkdownContent('# Error\n\nFailed to load the credit controlling policies document.');
      } finally {
        setIsLoading(false);
      }
    };

    if (status === 'active') {
      loadMarkdownContent();
    }
  }, [status]);

  const handleDownloadPDF = async () => {
    setIsDownloading(true);
    try {
      // Get the markdown content element
      const element = document.querySelector('.markdown-content');
      if (!element) {
        throw new Error('Content not found');
      }

      // Create canvas from the element with error handling for CORS issues
      let canvas: HTMLCanvasElement;
      try {
        canvas = await html2canvas(element as HTMLElement, {
          scale: 2, // Higher quality
          useCORS: true,
          allowTaint: false, // Set to false to prevent canvas tainting
          backgroundColor: '#ffffff'
        });
      } catch (corsError) {
        console.warn('CORS error with useCORS enabled, falling back to allowTaint mode:', corsError);
        // Fallback: try with allowTaint: true if CORS fails
        try {
          canvas = await html2canvas(element as HTMLElement, {
            scale: 2,
            useCORS: false,
            allowTaint: true,
            backgroundColor: '#ffffff'
          });
        } catch (fallbackError) {
          console.error('Both CORS and fallback methods failed:', fallbackError);
          throw new Error('Failed to generate PDF due to image loading issues. Please ensure all images are accessible.');
        }
      }

      // Create PDF with proper margins
      const imgData = canvas.toDataURL('image/png');
      
      // Define margins (20mm on all sides)
      const margin = 20;
      const pageWidth = 210; // A4 width in mm
      const a4PageHeight = 297; // A4 height in mm
      const contentWidth = pageWidth - (2 * margin);
      const contentHeight = a4PageHeight - (2 * margin);
      
      // Logo is a watermark overlay, doesn't affect content margins
      const availableContentHeight = contentHeight;
      
      // Calculate image dimensions with margins
      const imgWidth = contentWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Add logo watermark and page number to each page
      const addWatermark = async (pdf: jsPDF, pageNumber: number) => {
        // Add ARiA logo as watermark overlay (Z-order on top)
        const logoSize = 20; // 20x20mm square logo
        const logoX = pageWidth - margin - logoSize;
        const logoY = margin;
        
        try {
          // Load the ARiA logo image
          const logoImg = new Image();
          logoImg.crossOrigin = 'anonymous';
          logoImg.src = '/ARiA-Logo-PrimaryLightBlue-Transparent-BG.png';
          
          // Wait for image to load
          await new Promise((resolve, reject) => {
            logoImg.onload = resolve;
            logoImg.onerror = reject;
          });
          
          // Add logo to PDF as overlay (will appear on top of content)
          pdf.addImage(logoImg, 'PNG', logoX, logoY, logoSize, logoSize);
        } catch (error) {
          // Fallback: add text placeholder if logo fails to load
          pdf.setFillColor(100, 150, 200);
          pdf.rect(logoX, logoY, logoSize, logoSize, 'F');
          pdf.setTextColor(255, 255, 255);
          pdf.setFontSize(10);
          pdf.text('ARiA', logoX + logoSize/2, logoY + logoSize/2 + 2, { align: 'center' });
        }
        
        // Add website link below logo
        pdf.setTextColor(0, 0, 255);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.text('https://meet-aria.com', logoX, logoY + logoSize + 3);
        
        // Add page number at bottom
        pdf.setTextColor(128, 128, 128);
        pdf.setFontSize(10);
        pdf.text(`Page ${pageNumber}`, pageWidth - margin - 20, a4PageHeight - 10);
      };

      // Check if content fits on one page
      const contentStartY = margin;
      const contentEndY = contentStartY + imgHeight;
      
      let pdf: jsPDF;
      
      // If content extends beyond the page, we need to split it
      if (contentEndY > a4PageHeight - margin) {
        // Calculate how many pages we need
        const totalPages = Math.ceil(imgHeight / availableContentHeight);
        
        // Create new PDF with proper pagination
        pdf = new jsPDF('p', 'mm', 'a4');
        
        for (let page = 1; page <= totalPages; page++) {
          if (page > 1) {
            pdf.addPage();
          }
          
          // Calculate the portion of content for this page
          const startY = (page - 1) * availableContentHeight;
          const endY = Math.min(startY + availableContentHeight, imgHeight);
          const pageContentHeight = endY - startY;
          
          // Create a canvas for this page's content
          const pageCanvas = document.createElement('canvas');
          const pageCtx = pageCanvas.getContext('2d');
          const scale = canvas.width / imgWidth;
          pageCanvas.width = canvas.width;
          pageCanvas.height = pageContentHeight * scale;
          
          // Draw the appropriate portion
          if (pageCtx) {
            pageCtx.drawImage(
              canvas,
              0, startY * scale, canvas.width, pageContentHeight * scale,
              0, 0, canvas.width, pageContentHeight * scale
            );
          }
          
          // Convert to image data
          const pageImgData = pageCanvas.toDataURL('image/png');
          
          // Add content to PDF first (bottom layer)
          pdf.addImage(
            pageImgData, 
            'PNG', 
            margin, 
            margin, 
            imgWidth, 
            pageContentHeight
          );
          
          // Add logo watermark on top (Z-order overlay)
          await addWatermark(pdf, page);
        }
      } else {
        // Content fits on one page, add content first then logo overlay
        pdf = new jsPDF('p', 'mm', 'a4');
        
        // Add content first (bottom layer)
        pdf.addImage(imgData, 'PNG', margin, margin, imgWidth, imgHeight);
        
        // Add logo watermark on top (Z-order overlay)
        await addWatermark(pdf, 1);
      }

      // Download the PDF
      pdf.save('Credit_Control_Policy_SOP.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };


  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="flex items-center mb-4 sm:mb-0">
          <div className={`flex items-center justify-center w-12 h-12 rounded-full text-2xl mr-4 ${
            status === 'active' ? 'bg-purple-100 text-purple-600' :
            'bg-gray-100 text-gray-400'
          }`}>
            📋
          </div>
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-gray-900">Personalize Credit Controlling & Account Receivable Policies SOP</h3>
            <p className="text-sm sm:text-base text-gray-600">
              {status === 'active' ? 'Review and customize your credit control policies' :
               'Waiting for Xero authorization'}
            </p>
          </div>
        </div>
        
        {status === 'active' && (
          <button
            onClick={handleDownloadPDF}
            disabled={isDownloading}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center sm:justify-start"
          >
            {isDownloading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Generating...
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download PDF
              </>
            )}
          </button>
        )}
      </div>

      {status === 'waiting' && (
        <div className="bg-gray-50 rounded-lg p-6 text-center">
          <div className="text-4xl mb-4">⏳</div>
          <h4 className="text-lg font-semibold text-gray-700 mb-2">Waiting for Xero Connection</h4>
          <p className="text-gray-600">
            Complete the Xero authorization above to access credit control policies.
          </p>
        </div>
      )}

      {status === 'active' && (
        <div>
          {isLoading ? (
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading credit controlling policies...</p>
            </div>
          ) : (
            <div ref={markdownRef} className="prose prose-sm sm:prose-lg max-w-none markdown-content" style={{
              fontSize: '14px',
              lineHeight: '1.5',
              color: '#374151'
            }}>
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: (props: any) => <h1 {...props} style={{fontSize: '1.5rem', fontWeight: 'bold', marginTop: '0.75rem', marginBottom: '0.375rem', color: '#1f2937'}} />,
                  h2: H2WithComment,
                  h3: (props: any) => <h3 {...props} style={{fontSize: '1.125rem', fontWeight: 'bold', marginTop: '0.5rem', marginBottom: '0.25rem', color: '#1f2937'}} />,
                  p: (props: any) => <p {...props} style={{marginBottom: '0.375rem', lineHeight: '1.5'}} />,
                  ul: (props: any) => <ul {...props} style={{marginBottom: '0.375rem', paddingLeft: '1.25rem', listStyleType: 'disc'}} />,
                  ol: (props: any) => <ol {...props} style={{marginBottom: '0.375rem', paddingLeft: '1.25rem', listStyleType: 'decimal'}} />,
                  li: (props: any) => <li {...props} style={{marginBottom: '0.125rem', lineHeight: '1.5'}} />,
                  table: (props: any) => <table {...props} style={{width: '100%', borderCollapse: 'collapse', marginBottom: '0.375rem', fontSize: '0.875rem'}} />,
                  th: (props: any) => <th {...props} style={{border: '1px solid #d1d5db', padding: '0.375rem', backgroundColor: '#f9fafb', fontWeight: 'bold', textAlign: 'left', fontSize: '0.875rem'}} />,
                  td: (props: any) => <td {...props} style={{border: '1px solid #d1d5db', padding: '0.375rem', fontSize: '0.875rem'}} />,
                  code: (props: any) => <code {...props} style={{backgroundColor: '#f3f4f6', padding: '0.125rem 0.25rem', borderRadius: '0.25rem', fontSize: '0.75rem'}} />,
                  strong: (props: any) => <strong {...props} style={{fontWeight: 'bold'}} />,
                  em: (props: any) => <em {...props} style={{fontStyle: 'italic'}} />,
                  hr: (props: any) => <hr {...props} style={{border: 'none', borderTop: '1px solid #e5e7eb', margin: '0.75rem 0'}} />
                }}
              >
                {markdownContent}
              </ReactMarkdown>
            </div>
          )}
        </div>
      )}

      {status === 'active' && onNextStep && (
        <div className="flex justify-end mt-6 pt-6 border-t border-gray-200">
          <button
            onClick={onNextStep}
            className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg font-medium transition-colors flex items-center"
          >
            <span className="mr-2">✓</span>
            Continue
          </button>
        </div>
      )}

      {/* Comment Modal */}
      {showCommentModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="comment-modal-title"
        >
          <FocusTrap>
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <h3 id="comment-modal-title" className="text-lg font-semibold text-gray-900 mb-4">
                {comments[currentSectionId] ? `Edit comment about ${currentSectionTitle}` : `Add comment for "${currentSectionTitle}"`}
              </h3>
              
              <div className="mb-4">
                <label htmlFor="comment-text" className="block text-sm font-medium text-gray-700 mb-2">
                  Your comment about this section
                </label>
                <textarea
                  ref={textareaRef}
                  id="comment-text"
                  value={currentComment}
                  onChange={(e) => setCurrentComment(e.target.value)}
                  placeholder="Enter your comment about this section..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows={4}
                  aria-describedby="comment-text-help"
                />
                <div id="comment-text-help" className="text-xs text-gray-500 mt-1">
                  Share your thoughts or questions about this section
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleCancelComment}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                  type="button"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveComment}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                  type="button"
                >
                  {comments[currentSectionId] ? 'Update' : 'Save'}
                </button>
              </div>
            </div>
          </FocusTrap>
        </div>
      )}
    </div>
  );
};

export default CreditControllingPoliciesStep;
